import React from "react";
import { Search } from "lucide-react";
import { Input } from "../ui/input";
import SmartHeader from "./SmartHeader";

// Griglia skeleton di caricamento.
export function SkeletonGrid({ count = 8 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="list-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl bg-[#131316] border border-white/10 overflow-hidden animate-pulse">
          <div className="w-full aspect-[4/3] bg-white/5" />
          <div className="p-4 space-y-2">
            <div className="h-4 bg-white/10 rounded w-3/4" />
            <div className="h-3 bg-white/5 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, message }) {
  return (
    <div className="py-20 text-center border border-white/10 rounded-2xl bg-[#131316]" data-testid="list-empty">
      {Icon && <Icon className="h-10 w-10 mx-auto text-white/20 mb-3" />}
      <p className="text-white/50 text-sm">{message}</p>
    </div>
  );
}

// Chip filtro (pillola).
export function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-sm border whitespace-nowrap transition-colors ${
        active ? "bg-[#00E676]/20 border-[#00E676]/50 text-[#00E676]" : "border-white/10 bg-white/5 hover:bg-white/10 text-white/70"
      }`}
    >
      {children}
    </button>
  );
}

// Contenitore standard di una pagina-lista: header + hero titolo + barra controlli (ricerca/ordinamento/filtri) + contenuto.
export default function ListPageLayout({
  title, subtitle, icon: Icon, search, onSearch, searchPlaceholder = "Cerca…",
  sort, onSort, sortOptions, chips, children,
}) {
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-20">
      <SmartHeader back />
      <div className="max-w-6xl mx-auto px-3 sm:px-6">
        <div className="py-6">
          <h1 className="font-['Unbounded'] font-bold text-2xl md:text-3xl tracking-tight flex items-center gap-2.5">
            {Icon && <Icon className="h-7 w-7 text-[#00E676]" />} {title}
          </h1>
          {subtitle && <p className="text-white/50 text-sm mt-1.5">{subtitle}</p>}
        </div>

        <div className="sticky top-[61px] z-30 bg-[#0A0A0C]/90 backdrop-blur-md py-3 border-b border-white/10 mb-6 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {onSearch && (
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input data-testid="list-search" value={search} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} className="pl-10 h-11 rounded-full bg-[#131316] border-white/10" />
              </div>
            )}
            {sortOptions && (
              <select data-testid="list-sort" value={sort} onChange={(e) => onSort(e.target.value)} className="h-11 rounded-full bg-[#131316] border border-white/10 px-4 text-sm text-white/80 focus:outline-none focus:ring-2 focus:ring-[#00E676] sm:w-52">
                {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
          </div>
          {chips && chips.length > 0 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">{chips}</div>
          )}
        </div>

        {children}
      </div>
    </div>
  );
}
